// ── WHAT THIS COVERAGE WOULD HAVE PAID — ON REVENUE, NOT YIELD ───────────────
//
// Kevin, 2026-09-23: *"You are supposed to look at 10 year APH database for each
// farmer and decide what their optimal coverage would've been across those 10 years
// based on their yield AND PRICE MOVEMENT from guarantee to harvest price."*
//
// He is right, and the reason the engine did not was a category error. `individualNeed`
// prices every historical shortfall at ONE price and says so:
//
//   "we do not hold a farm-level harvest-price history, so revenue is approximated
//    as `yield x projected price`"
//
// ⚠ RP DOES NOT SETTLE ON A FARM-LEVEL PRICE. It settles on RMA's PUBLISHED projected
// and harvest prices, which are per (crop year, state, practice, type) — and this
// database already holds them. Measured 2026-09-23 over `rma_prices` commodity 0011:
// **20,334 rows, 2015–2026 complete on both prices** for all five states this book
// writes in, and **county does not matter** (378 (year, state, practice, type) groups,
// ZERO where counties disagree).
//
// The movement it was throwing away is not small — NY conventional wheat:
//   2021 $5.60 -> $6.68 (+19%)   2023 $8.45 -> $6.83 (-19%)   2024 $6.72 -> $5.51 (-18%)
//   2022 $7.16 -> $8.12 (+13%)   2025 $6.06 -> $5.43 (-10%)   2026 $5.76 -> $6.52 (+13%)
//
// ⚠ AND IT MOVES THE ANSWER IN BOTH DIRECTIONS, which is why it is a simulation and
// not a correction. A price that ROSE into a short crop pays MORE than the yield model
// showed (RP's harvest-price provision); a price that FELL pays more on a NORMAL crop,
// because the revenue guarantee is struck on the projected price and the crop is valued
// at the lower harvest one. The yield model cannot see either.
//
// ── THE SETTLEMENT, PER PLAN ─────────────────────────────────────────────────
// Revenue Protection (RP), 7 CFR 457.8 — the guarantee is struck on the HIGHER of the
// projected and harvest prices, and production is valued at the harvest price:
//
//     guarantee    = APH x level x max(projected, harvest)
//     to count     = actual yield x harvest
//     indemnity    = max(0, guarantee - to count) x share
//
// RP with the Harvest Price Exclusion (RP-HPE) drops the `max`: the guarantee is struck
// on the projected price only, while production is STILL valued at harvest.
// Yield Protection (YP) is not a revenue product at all — the shortfall is in bushels
// and is paid at the projected price, so both sides use `projected` and the result is
// the yield model exactly. That equivalence is asserted in the checker rather than
// assumed, because it is the one case where the two bases must agree to the cent.
//
// ⚠ A YEAR WITH NO PUBLISHED PRICE IS SKIPPED AND COUNTED, NEVER DEFAULTED. Carrying
// a missing harvest price forward from the projected one would silently convert RP into
// RP-HPE for that year and understate it.

const r4 = (n) => (n == null || !isFinite(n) ? null : Math.round(n * 10000) / 10000);

// The three plans this book writes, and what each does with the two prices.
export const REVENUE_PLAN = { RP: "RP", RP_HPE: "RP-HPE", YP: "YP" };

// `mpci_plan_code` / `plan` as the AIP states them -> the settlement rule.
// 02 is Revenue Protection, 03 is RP with the Harvest Price Exclusion, 01 is Yield
// Protection. A CAT policy is YP at a 0.55 price election and rates as YP here.
export function revenuePlanOf({ plan = null, mpciPlanCode = null } = {}) {
  const code = String(mpciPlanCode || "").trim();
  if (code === "02") return REVENUE_PLAN.RP;
  if (code === "03") return REVENUE_PLAN.RP_HPE;
  if (code === "01" || code === "90") return REVENUE_PLAN.YP;
  const p = String(plan || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (p === "RPHPE" || p === "RPH") return REVENUE_PLAN.RP_HPE;
  if (p === "YP" || p === "APH") return REVENUE_PLAN.YP;
  if (p === "RP") return REVENUE_PLAN.RP;
  return null;                                  // unknown — the caller decides, never a guess
}

// ── ⚠ THE APH MOVES, AND THE GUARANTEE MOVES WITH IT ────────────────────────
// Kevin, 2026-09-23: *"You also need to take into account what their APH was each year
// as those change."* He is right, and the first cut of this file was wrong about it —
// it priced every historical year's guarantee on TODAY's approved yield. A farm whose
// APH has climbed from 55 to 75 would have had materially smaller guarantees in its
// early years, so replaying them at 75 invents indemnities that could not have been
// paid; a farm whose APH has fallen gets the opposite error.
//
// ⚠ IT IS A RECONSTRUCTION, NOT RMA'S APPROVED YIELD, AND IT SAYS SO. `crop_coverage`
// holds the AIP's own stated approved yield for CY2026 and CY2027 ONLY — two years,
// measured 2026-09-23 — so a ten-year replay cannot read it and has to rebuild it.
// This is the simple average of the up-to-10 production years BEFORE the year being
// priced, which is RMA's own rule for a full database.
//
// ⚠ WHAT IT DELIBERATELY DOES NOT DO is substitute T-yields into a short database
// (7 CFR 400.52: 65% / 80% / 90% of the transitional yield for zero, one and two years
// of records). `computeApprovedYield2027` implements that correctly but is pinned to
// 2027's county T-yield tables and cannot answer for 2019. So instead of approximating
// a substitution, a year without `MIN_PRIOR_YEARS` of history behind it is SKIPPED and
// COUNTED — the same discipline as a year with no published price. That costs the first
// few years of every database and is the honest trade: a reconstructed APH built on one
// prior year is not an approved yield, and an indemnity replayed against it is fiction.
export const MIN_PRIOR_YEARS = 4;     // RMA: "At least four yields will always exist in the database"
export const APH_WINDOW = 10;         // the database is the 10 most recent crop years

// The approved yield as it would have stood for `year`, from this unit's own record.
// Returns null where too little history precedes it.
export function rollingAph(series, year, minPrior = MIN_PRIOR_YEARS) {
  const prior = (series || [])
    .filter((o) => o && o.y != null && isFinite(o.y) && o.y > 0 && +o.yr < +year)
    .sort((a, b) => b.yr - a.yr)
    .slice(0, APH_WINDOW);
  if (prior.length < minPrior) return null;
  return prior.reduce((s, o) => s + o.y, 0) / prior.length;
}

// The per-acre indemnity ONE year's actual yield would have earned at ONE coverage
// level. Exported because the band slice below and the checker both need it, and a
// second copy of a settlement rule is how two surfaces come to disagree about a claim.
export function yearIndemnity({ aph, level, yieldPerAcre, projected, harvest, plan }) {
  const A = +aph, L = +level, Y = +yieldPerAcre, P = +projected, H = +harvest;
  if (!(A > 0) || !(L > 0) || !(Y >= 0) || !(P > 0) || !(H > 0)) return null;
  const guarPrice = plan === REVENUE_PLAN.RP ? Math.max(P, H)
                  : plan === REVENUE_PLAN.YP ? P
                  : P;                                   // RP-HPE: projected only
  const countPrice = plan === REVENUE_PLAN.YP ? P : H;   // YP counts bushels at projected
  const guarantee = A * L * guarPrice;
  const toCount = Y * countPrice;
  return { guarantee: r4(guarantee), toCount: r4(toCount),
           indemnity: r4(Math.max(0, guarantee - toCount)) };
}

// ── THE BAND ─────────────────────────────────────────────────────────────────
// What the slice of coverage between `lo` and `hi` would have paid, averaged over the
// farm's own years. Mirrors `bandExpectedIndemnity`'s shape exactly so the two are
// interchangeable at the call site — the ONLY difference is that this one prices each
// year at that year's own two published prices.
export function bandExpectedIndemnityRevenue({
  series, aph, lo, hi, prices, plan, share = 1, minYears = 5,
  // RMA's stated approved yield per crop year where a caller has it; otherwise the
  // rolling reconstruction above fills in. { [cropYear]: approvedYield }.
  aphAsOf = null, minPriorYears = MIN_PRIOR_YEARS,
}) {
  const A = +aph;
  if (!(A > 0) || !(hi > lo) || !plan) return null;
  const s = (series || []).filter((o) => o && o.y != null && isFinite(o.y) && o.y > 0);
  if (!s.length) return null;

  let sum = 0, hits = 0, used = 0;
  const skipped = [], noAph = [];
  for (const o of s) {
    const px = prices && prices[o.yr];
    const P = px && +px.projected, H = px && +px.harvest;
    if (!(P > 0) || !(H > 0)) { skipped.push(o.yr); continue; }
    // ⚠ THE YEAR'S OWN APH, not today's. `aphAsOf` lets a caller supply RMA's stated
    // figure where it has one; otherwise it is reconstructed from the years before it,
    // and a year without enough history behind it is skipped rather than priced on a
    // guarantee the grower did not hold.
    const Ay = (aphAsOf && +aphAsOf[o.yr] > 0) ? +aphAsOf[o.yr] : rollingAph(s, o.yr, minPriorYears);
    if (!(Ay > 0)) { noAph.push(o.yr); continue; }
    const at = (L) => {
      const r = yearIndemnity({ aph: Ay, level: L, yieldPerAcre: o.y, projected: P, harvest: H, plan });
      return r ? r.indemnity : 0;
    };
    // The slice between the two levels — the same subtraction the yield model does in
    // bushels, done in dollars so the year's own price movement is inside both terms.
    const slice = Math.max(0, at(hi) - (lo > 0 ? at(lo) : 0));
    if (slice > 0) hits++;
    sum += slice;
    used++;
  }
  // ⚠ THE FLOOR IS ON YEARS ACTUALLY PRICED, not on years on file. A unit with ten
  // yield years and two priced ones must not report an average over two and call it a
  // decade — that is the truncated-read class this repo already records.
  if (used < minYears) {
    return { perAcre: null, yearsPaying: null, years: used, yearsOnFile: s.length,
             skippedYears: skipped, noAphYears: noAph, basis: "revenue", plan,
             reason: used + " of " + s.length + " year" + (s.length === 1 ? "" : "s")
                   + " have a published price pair AND enough prior history for that year's APH (need " + minYears + ")" };
  }
  return { perAcre: r4((sum / used) * (+share > 0 ? +share : 1)),
           yearsPaying: hits, years: used, yearsOnFile: s.length,
           skippedYears: skipped, noAphYears: noAph, basis: "revenue", plan };
}

// ── THE WHOLE LADDER, WHICH IS WHAT "OPTIMAL" MEANS ──────────────────────────
// For each coverage level, what it would have paid per acre averaged over the farm's
// own record. The CALLER compares that against what each level costs — this function
// deliberately knows nothing about premium, so it cannot be tuned to a conclusion.
export function revenueLadder({ series, aph, levels, prices, plan, share = 1, minYears = 5 }) {
  const A = +aph;
  if (!(A > 0) || !plan || !Array.isArray(levels) || !levels.length) return null;
  const out = {};
  for (const L of levels) {
    out[L] = bandExpectedIndemnityRevenue({ series, aph: A, lo: 0, hi: L, prices, plan, share, minYears });
  }
  return out;
}

// One sentence a human can check, naming the sample and the plan rather than implying
// a precision the record does not carry.
export function revenueBasisNote(b) {
  if (!b) return null;
  if (b.perAcre == null) return b.reason || "not enough priced years to simulate this band";
  const skip = (b.skippedYears || []).length;
  return "Replayed over " + b.years + " of this farm's own yield years at each year's "
       + "published projected and harvest price, settled as " + b.plan
       + (skip ? " (" + skip + " year" + (skip === 1 ? "" : "s") + " had no published price and were left out)" : "")
       + ".";
}
